<script lang="ts">
  import { onMount, onDestroy } from "svelte";
  import type { Document } from "../../../services/api/types.js";
  import Chatbot2 from "./Chatbot2.svelte";
  import { Splitpanes, Pane } from "svelte-splitpanes";
  import { FontAwesomeIcon } from "@fortawesome/svelte-fontawesome";
  import { faFileAlt, faFolder } from "@fortawesome/free-solid-svg-icons";
  import { userStore } from "../../../services/stores/user-store.js";
  import { saveUserMetadata } from "../../../services/api/data-handler.js";
  import { get } from "svelte/store";
  import { logError, logInfo, logWarning } from "../../../services/logging/log-handler.js";

  // Subscribe to userStore for reactivity
  $: $userStore;

  let customer = "";
  let documents: Document[] = [];
  let projects: any[] = [];

  // Splitter position
  const MIN_WIDTH_REM = 10;
  
  // Initialize with default calculated value immediately
  function calculateDefaultPosition(): number {
    const viewportWidth = window.innerWidth;
    const remInPixels = 16; // Base font size
    const minWidthPixels = MIN_WIDTH_REM * remInPixels;
    return (minWidthPixels / viewportWidth) * 100;
  }

  let splitPosition: number = calculateDefaultPosition();
  let saveTimeout: ReturnType<typeof setTimeout> | null = null;
  let hasLoadedPosition = false;
  let lastSavedPosition: number | null = null; // Track last saved to avoid duplicate saves
  let pendingSave = false; // Track if there's a pending save

  // Load splitter position from user metadata when user store is ready
  $: {
    if ($userStore.user && !hasLoadedPosition) {
      if ($userStore.user.metadata?.content_context_splitter) {
        const saved = $userStore.user.metadata.content_context_splitter;
        if (typeof saved === "number" && saved > 0 && saved < 100) {
          splitPosition = saved;
          lastSavedPosition = saved;
          hasLoadedPosition = true;
        } else {
          hasLoadedPosition = true;
        }
      } else {
        hasLoadedPosition = true;
      }
    }
  }

  // Actually perform the save
  async function doSave() {
    // Skip if position hasn't changed from last save
    if (lastSavedPosition !== null && Math.abs(splitPosition - lastSavedPosition) < 0.5) {
      pendingSave = false;
      return;
    }
    
    try {
      const store = get(userStore);
      if (!store.user) return;
      const newMetadata = {
        ...store.user.metadata,
        content_context_splitter: splitPosition,
      };
      await saveUserMetadata(newMetadata);
      lastSavedPosition = splitPosition;
    } catch (error) {
      logError(error, "ChatPopup", undefined, { message: "Error saving splitter position" });
    } finally {
      pendingSave = false;
    }
  }

  // Save splitter position to user metadata (debounced)
  function saveSplitterPosition() {
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }
    pendingSave = true;
    saveTimeout = setTimeout(() => {
      doSave();
      saveTimeout = null;
    }, 1000); // Debounce by 1000ms (increased from 500ms)
  }

  // Handle splitter resize
  function handleSplitterResized(event: CustomEvent) {
    const panes = event.detail as Array<{ size: number; min?: number; max?: number }>;
    if (panes && Array.isArray(panes) && panes.length > 0 && panes[0]?.size !== undefined) {
      splitPosition = panes[0].size;
      saveSplitterPosition();
    }
  }

  function safeGet(data: any, path: string, defaultValue: any = "N/A") {
    try {
      return (
        path
          .split(".")
          .reduce((acc: any, part: string) => acc?.[part], data as any) ??
        defaultValue
      );
    } catch {
      return defaultValue;
    }
  }

  function getDocumentTitle(document: any): string {
    return document.doc_name || document.doc_title || document.file_name || "Untitled Document";
  }

  function getProjectName(project: any): string {
    return project.name || project.project_id || "Untitled Project";
  }

  onMount(() => {
    // Get parameters from URL
    const urlParams = new URLSearchParams(window.location.search);
    customer = urlParams.get("customer") || "";

    // Parse documents from URL parameters
    const documentsParam = urlParams.get("documents");
    if (documentsParam) {
      try {
        documents = JSON.parse(documentsParam);
      } catch (error) {
        logError(error, "ChatPopup", undefined, { message: "Error parsing documents from URL" });
        documents = [];
      }
    }

    // Parse projects from URL parameters
    const projectsParam = urlParams.get("projects");
    if (projectsParam) {
      try {
        const projectIds = JSON.parse(projectsParam);
        projects = projectIds.map((id: string) => ({ project_id: id }));
      } catch (error) {
        logError(error, "ChatPopup", undefined, { message: "Error parsing projects from URL" });
        projects = [];
      }
    }
  });

  onDestroy(() => {
    if (saveTimeout) {
      clearTimeout(saveTimeout);
      saveTimeout = null;
    }
    // Save immediately on destroy if there's a pending save
    if (pendingSave) {
      doSave();
    }
  });
</script>

<div class="document-content-layout">
  <Splitpanes class="my-theme" on:resized={handleSplitterResized}>
    <!-- Left Panel - Context Panel -->
    <Pane size={splitPosition} minSize={((MIN_WIDTH_REM * 16) / window.innerWidth) * 100}>
      {#if documents.length > 0}
        <div class="documents-panel">
          <div class="documents-header">
            <div class="sub-tab">CONTEXT</div>
          </div>
          <div class="documents-list">
            <div class="documents-list-label">
              {documents.length > 1 ? "Documents" : "Document"}
            </div>
            {#each documents as document}
              <div class="document-item">
                <div class="document-item-content">
                  <FontAwesomeIcon icon={faFileAlt} class="document-icon" />
                  <div class="document-title text-value">
                    {getDocumentTitle(document)}
                  </div>
                </div>
                {#if safeGet(document, "stage") !== "N/A"}
                  <div class="area-content-narrow ml-4">
                    <span class="text-label">Stage:</span>
                    <p class="text-value">{safeGet(document, "stage")}</p>
                  </div>
                {/if}
                {#if safeGet(document, "project") !== "N/A"}
                  <div class="area-content-narrow ml-4">
                    <span class="text-label">Project:</span>
                    <p class="text-value">{safeGet(document, "project")}</p>
                  </div>
                {/if}
              </div>
            {/each}
          </div>
        </div>
      {:else if projects.length > 0}
        <div class="projects-panel">
          <div class="projects-header">
            <div class="sub-tab">CONTEXT</div>
          </div>
          <div class="projects-list">
            <div class="projects-list-label">
              {projects.length > 1 ? "Projects" : "Project"}
            </div>
            {#each projects as project}
              <div class="project-item">
                <div class="project-item-content">
                  <FontAwesomeIcon icon={faFolder} class="project-icon mr-1" />
                  <p class="project-name text-value">{getProjectName(project)}</p>
                </div>
              </div>
            {/each}
          </div>
        </div>
      {:else}
        <div class="documents-panel">
          <div class="documents-header">
            <div class="sub-tab">CONTEXT</div>
          </div>
          <div class="documents-list">
            <div class="no-documents">
              <div class="placeholder animate-pulse"></div>
              <div class="placeholder animate-pulse"></div>
              <div class="placeholder animate-pulse"></div>
            </div>
          </div>
        </div>
      {/if}
    </Pane>

    <!-- Right Panel - Chat -->
    <Pane>
      <div class="chat-panel flex flex-col">
        <Chatbot2 external={true} customer_code={customer} {documents} {projects} />
      </div>
    </Pane>
  </Splitpanes>
</div>
